import React, { useMemo } from 'react';
import { FileText, TrendingUp, Calendar, Building, Package, DollarSign } from 'lucide-react';

interface ClassificationTableProps {
  results: any;
}

interface LineItemRow {
  sourceFile: string;
  documentId: string;
  date: string;
  vendor: string;
  item: string;
  quantity: number | string;
  price: number | string;
  total: number | string;
  confidence: number;
  currency: string;
  // Enhanced: Add type to distinguish between line items and receipt summaries
  itemType: 'line_item' | 'subtotal' | 'tax' | 'tip' | 'total';
}

const ClassificationTable: React.FC<ClassificationTableProps> = ({ results }) => {
  // Process results into line items and vendor summaries
  const { lineItems, vendorSummaries, financialSummary } = useMemo(() => {
    if (!results || !results.rawResults) {
      return { lineItems: [], vendorSummaries: [], financialSummary: { totalAmount: 0, dateRange: null } };
    }

    const items: LineItemRow[] = [];
    const vendorMap = new Map<string, { 
      receiptCount: number; 
      totalSpent: number; 
      currency: string;
      dates: string[];
    }>();
    let totalAmount = 0;
    const allDates: string[] = [];

    results.rawResults.forEach((fileResult: any) => {
      const { documentName, extractedData, confidence } = fileResult;
      const { transactionDate, merchantName, items: receiptItems, total, currency } = extractedData;
      
      const vendor = merchantName || 'Unknown';
      const receiptTotal = total?.amount || 0;
      const receiptCurrency = currency || 'USD';
      
      // Track vendor spending
      if (!vendorMap.has(vendor)) {
        vendorMap.set(vendor, { 
          receiptCount: 0, 
          totalSpent: 0, 
          currency: receiptCurrency,
          dates: []
        });
      }
      const vendorData = vendorMap.get(vendor)!;
      vendorData.receiptCount++;
      vendorData.totalSpent += receiptTotal;
      if (transactionDate && transactionDate !== 'N/A') {
        vendorData.dates.push(transactionDate);
        allDates.push(transactionDate);
      }
      
      // Track total spending
      totalAmount += receiptTotal;

      // Only add individual line items (no summary items)
      if (receiptItems && receiptItems.length > 0) {
        receiptItems.forEach((item: any, index: number) => {
          items.push({
            sourceFile: documentName,
            documentId: fileResult.documentId,
            date: transactionDate || 'N/A',
            vendor: vendor,
            item: item.description || `Item ${index + 1}`,
            quantity: item.quantity || 1,
            price: item.unitPrice || 'N/A',
            total: item.totalPrice || 'N/A',
            confidence: confidence,
            currency: item.currency || receiptCurrency,
            itemType: 'line_item'
          });
        });
      } else {
        // If no line items, show one summary row
        items.push({
          sourceFile: documentName,
          documentId: fileResult.documentId,
          date: transactionDate || 'N/A',
          vendor: vendor,
          item: 'Receipt Total',
          quantity: 1,
          price: receiptTotal,
          total: receiptTotal,
          confidence: confidence,
          currency: receiptCurrency,
          itemType: 'line_item'
        });
      }
    });

    // Convert vendor map to array
    const vendorSummaries = Array.from(vendorMap.entries()).map(([vendor, data]) => ({
      vendor,
      receiptCount: data.receiptCount,
      totalSpent: data.totalSpent,
      currency: data.currency,
      dateRange: data.dates.length > 0 ? {
        earliest: data.dates.sort()[0],
        latest: data.dates.sort()[data.dates.length - 1]
      } : null
    }));

    // Calculate overall date range
    const financialSummary = {
      totalAmount,
      dateRange: allDates.length > 0 ? {
        earliest: allDates.sort()[0],
        latest: allDates.sort()[allDates.length - 1]
      } : null
    };

    return { lineItems: items, vendorSummaries, financialSummary };
  }, [results]);

  const formatCurrency = (amount: number | string, currency: string = 'USD') => {
    if (amount === 'N/A' || amount === null || amount === undefined) return 'N/A';
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return 'N/A';
    
    // Use proper currency symbol mapping
    const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    return `${currencySymbol}${numAmount.toFixed(2)}`;
  };

  const formatDateRange = (dateRange: { earliest: string; latest: string } | null) => {
    if (!dateRange) return 'No dates available';
    if (dateRange.earliest === dateRange.latest) return dateRange.earliest;
    return `${dateRange.earliest} - ${dateRange.latest}`;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400';
    if (confidence >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (!results) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-500 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-gray-300 mb-2">No Classification Results</h3>
        <p className="text-gray-500">Run document classification to see results here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Summary Stats */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Classification Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{results.totalDocuments}</div>
            <div className="text-sm text-gray-400">Documents</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{results.successfulClassifications}</div>
            <div className="text-sm text-gray-400">Successful</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{lineItems.length}</div>
            <div className="text-sm text-gray-400">Line Items</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-400">{vendorSummaries.length}</div>
            <div className="text-sm text-gray-400">Vendors</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">
              {formatCurrency(financialSummary.totalAmount, 'USD')}
            </div>
            <div className="text-sm text-gray-400">Total Amount</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-cyan-400">
              {formatDateRange(financialSummary.dateRange)}
            </div>
            <div className="text-sm text-gray-400">Date Range</div>
          </div>
        </div>
      </div>

      {/* Vendor Breakdown */}
      {vendorSummaries.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">Vendor Summary</h3>
            <p className="text-sm text-gray-400 mt-1">
              Spending breakdown by vendor
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-750">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Building className="w-4 h-4" />
                      <span>Vendor</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Receipt Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4" />
                      <span>Total Spent</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Date Range</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {vendorSummaries.map((vendor, index) => (
                  <tr key={index} className="bg-gray-800 hover:bg-gray-750 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-200 font-medium">{vendor.vendor}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{vendor.receiptCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300 font-medium">
                        {formatCurrency(vendor.totalSpent, vendor.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {formatDateRange(vendor.dateRange)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Line Items Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Receipt Line Items</h3>
          <p className="text-sm text-gray-400 mt-1">
            Individual items extracted from classified receipts
          </p>
        </div>

        {lineItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-750">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>Source File</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Date</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Building className="w-4 h-4" />
                      <span>Vendor</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4" />
                      <span>Item</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4" />
                      <span>Total</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-4 h-4" />
                      <span>Confidence</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {lineItems.map((item, index) => (
                  <tr key={index} className="bg-gray-800 hover:bg-gray-750 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-200 font-medium">{item.sourceFile}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{item.date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300 font-medium">{item.vendor}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{item.item}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{item.quantity}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {formatCurrency(item.price, item.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300 font-medium">
                        {formatCurrency(item.total, item.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${getConfidenceColor(item.confidence)}`}>
                        {(item.confidence * 100).toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <Package className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-300 mb-2">No Line Items Found</h4>
            <p className="text-gray-500">
              The classified documents don't contain detailed line item information.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassificationTable;